/* eslint-disable no-lonely-if */
import {
  TransitionProperties,
  TransitionState
} from "@website/ui/transition/transition.types";
import { FC, useEffect, useRef, useState } from "react";

const TransitionV2: FC<TransitionProperties> = ({
  in: inProperty = false,
  timeout,
  unmountOnExit = false,
  mountOnEnter = false,
  children,
  onEnter,
  onEntering,
  onEntered,
  onExit,
  onExiting,
  onExited,
  enter = true,
  exit = true
}) => {
  const [state, setState] = useState<TransitionState>("exited");
  const [initialRender, setInitialRender] = useState(
    mountOnEnter ? !inProperty : false
  );
  const timeoutReference = useRef<NodeJS.Timeout | null>(null);

  useEffect(
    () => () => {
      if (timeoutReference.current) clearTimeout(timeoutReference.current);
    },
    []
  );

  useEffect(() => {
    if (initialRender && inProperty) {
      setInitialRender(false);
    }
  }, [initialRender, inProperty]);

  useEffect(() => {
    if (initialRender) return;

    if (inProperty) {
      if (state === "exited") {
        if (!enter) {
          setState("entered");
          return;
        }

        onEnter?.();
        setState("entering");
        onEntering?.();

        const enterTimeout =
          typeof timeout === "number" ? timeout : timeout?.enter ?? 0;
        timeoutReference.current = setTimeout(() => {
          setState("entered");
          onEntered?.();
        }, enterTimeout);
      }
    } else if (state === "entered") {
      if (!exit) {
        setState("exited");
        return;
      }

      onExit?.();
      setState("exiting");
      onExiting?.();

      const exitTimeout =
        typeof timeout === "number" ? timeout : timeout?.exit ?? 0;
      timeoutReference.current = setTimeout(() => {
        setState("exited");
        onExited?.();
      }, exitTimeout);
    }
  }, [
    inProperty,
    state,
    enter,
    exit,
    timeout,
    onEnter,
    onEntering,
    onEntered,
    onExit,
    onExiting,
    onExited,
    initialRender
  ]);

  const showChild =
    (!mountOnEnter || !initialRender) && (!unmountOnExit || state !== "exited");

  if (!showChild) {
    return null;
  }

  return <>{children(state)}</>;
};

export { TransitionV2 };
